import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { updateRoomMutePreference } from '../lib/api';

type NotificationPermissionState = 'default' | 'granted' | 'denied' | 'unsupported';

interface NotificationsState {
  permission: NotificationPermissionState;
  requestPermission: () => Promise<void>;
  isMuted: (roomId: string) => boolean;
  isMuteUpdating: (roomId: string) => boolean;
  getMuteError: (roomId: string) => string | null;
  toggleMute: (roomId: string) => Promise<void>;
}

const NotificationsContext = createContext<NotificationsState | null>(null);

interface NotificationsProviderProps {
  children: ReactNode;
}

function readInitialPermission(): NotificationPermissionState {
  if (typeof Notification === 'undefined') return 'unsupported';
  return Notification.permission;
}

export function NotificationsProvider({ children }: NotificationsProviderProps) {
  const [permission, setPermission] = useState<NotificationPermissionState>(
    () => readInitialPermission(),
  );
  const [mutedRooms, setMutedRooms] = useState<Set<string>>(() => new Set());
  const [pendingMuteRooms, setPendingMuteRooms] = useState<Set<string>>(
    () => new Set(),
  );
  const [muteErrors, setMuteErrors] = useState<Map<string, string>>(
    () => new Map(),
  );

  const requestPermission = useCallback(async () => {
    if (typeof Notification === 'undefined') {
      setPermission('unsupported');
      return;
    }
    if (Notification.permission === 'granted' || Notification.permission === 'denied') {
      setPermission(Notification.permission);
      return;
    }
    const result = await Notification.requestPermission();
    setPermission(result);
  }, []);

  const isMuted = useCallback(
    (roomId: string) => mutedRooms.has(roomId),
    [mutedRooms],
  );

  const isMuteUpdating = useCallback(
    (roomId: string) => pendingMuteRooms.has(roomId),
    [pendingMuteRooms],
  );

  const getMuteError = useCallback(
    (roomId: string) => muteErrors.get(roomId) ?? null,
    [muteErrors],
  );

  const toggleMute = useCallback(async (roomId: string) => {
    const wasMuted = mutedRooms.has(roomId);
    const nextMuted = !wasMuted;

    setMuteErrors((prev) => {
      const next = new Map(prev);
      next.delete(roomId);
      return next;
    });
    setPendingMuteRooms((prev) => new Set(prev).add(roomId));
    setMutedRooms((prev) => {
      const next = new Set(prev);
      if (nextMuted) {
        next.add(roomId);
      } else {
        next.delete(roomId);
      }
      return next;
    });

    try {
      await updateRoomMutePreference(roomId, nextMuted);
    } catch {
      setMutedRooms((prev) => {
        const next = new Set(prev);
        if (wasMuted) {
          next.add(roomId);
        } else {
          next.delete(roomId);
        }
        return next;
      });
      setMuteErrors((prev) => {
        const next = new Map(prev);
        next.set(roomId, "Couldn't update notification setting. Try again.");
        return next;
      });
    } finally {
      setPendingMuteRooms((prev) => {
        const next = new Set(prev);
        next.delete(roomId);
        return next;
      });
    }
  }, [mutedRooms]);

  const value = useMemo(
    () => ({
      permission,
      requestPermission,
      isMuted,
      isMuteUpdating,
      getMuteError,
      toggleMute,
    }),
    [
      permission,
      requestPermission,
      isMuted,
      isMuteUpdating,
      getMuteError,
      toggleMute,
    ],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsState {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error('useNotifications must be used inside <NotificationsProvider>');
  }
  return ctx;
}

export interface NotifyOptions {
  title: string;
  body: string;
  tag?: string;
  onClick?: () => void;
}

// Side-effect helper that posts a desktop notification IF permission is
// granted + the Notification API is supported. Caller checks `permission`
// and `isMuted(roomId)` before invoking.
export function showDesktopNotification(opts: NotifyOptions): void {
  if (typeof Notification === 'undefined') return;
  if (Notification.permission !== 'granted') return;
  const n = new Notification(opts.title, { body: opts.body, tag: opts.tag });
  if (opts.onClick) {
    n.onclick = () => {
      opts.onClick?.();
      n.close();
      window.focus();
    };
  }
}
